import React, { type ReactNode, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip, {
	type PositionType,
	TooltipPrimitive,
	type TooltipPrimitiveProps,
} from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type Props = {
	tooltipContent: ReactNode;
	position?: PositionType;
};

export const InfoTooltip = ({ tooltipContent, position }: Props) => {
	const { formatMessage } = useIntl();
	return (
		<ToolTip
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			component={InlineDialog as ComponentType<TooltipPrimitiveProps>}
			content={tooltipContent}
			position={position}
		>
			<StyledButton
				aria-label={formatMessage(messages.infoLabel)}
				iconBefore={<EditorPanelIcon label="" />}
				testId="project-create-importer-upload-flow.common.ui.info-tooltip.info"
				appearance="subtle"
			/>
		</ToolTip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':hover': {
			backgroundColor: token('color.background.brand.bold.pressed', '#09326C'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'> span': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				color: token('elevation.surface', colors.N0),
			},
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineDialog = styled(TooltipPrimitive)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface', colors.N0),
	borderRadius: '4px',
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
	),
	maxHeight: '300px',
	maxWidth: '300px',
	boxSizing: 'content-box',
	padding: token('space.200', '16px'),
});
